import { Layout } from "providers/Layout";
import { useApplicantIdReportController } from "./controller";
import { FetchBoundary } from "providers/FetchBoundary";
import { useEffect, useMemo, useState } from "react";
import { LayoutHeader } from "./components/LayoutHeader";
import {
  Verification,
  VerificationNames,
  VerificationStatus
} from "shared/types";
import { combineVerifications } from "shared/utils";
import { CancelOrRefundModal } from "shared/app/CancelOrRefundModal";
import { ChangeRoleModal } from "shared/app/ChangeRoleModal";
import { ResendInviteModal } from "shared/app/ResendInviteModal";
import { PrintProvider } from "./providers/PrintProvider";
import { Redirect } from "shared/components/Redirect";
import { useAuthContext } from "providers/Auth";
import { FEATURES } from "types/landlord/partner";
import { Invitee, ApplicantType } from "shared/types";
import { useQueryClient, useMutation } from "@tanstack/react-query";
import { instance } from "api/apiBase";
import { toast } from "react-toastify";
import { getErrorMessage } from "utils/error";
import {
  filterVerificationByConfigValue,
  sortVerifications,
  verificationNameToTitle
} from "../applications-id/components/ApplicantsTable/utils";
import { VERIFICATIONS_ORDER } from "../applications-id/components/ApplicantsTable/constants";
import { mergeHousingHistoryVerifications } from "./utils/mergeHousingHistoryVerifications";
import { RefundModal } from "../../shared/app/RefundModal";
import { DocumentsRequestModal } from "shared/app/DocumentsRequestModal";
import { Loader } from "shared/components/Loader";
import { UncancelModal } from "shared/app/UncancelModal";
import { Show } from "shared/components";
import { SetAsPrimaryModal } from "shared/app/SetAsPrimaryModal";
import { Applicant } from "types/application";
import { ApplicationReportLayout } from "./components/ApplicationReportLayout";

export type TabStepsType = VerificationNames | "additional_documents" | "chat";

// Tabs that exist for every applicant regardless of role or template. Used to
// decide whether the current report can be kept when switching applicants.
const ALWAYS_AVAILABLE_REPORTS: TabStepsType[] = [
  VerificationNames.RentalApplication,
  "additional_documents",
  "chat"
];

// eslint-disable-next-line sonarjs/cognitive-complexity
export const ApplicationsIdReportView = () => {
  const [showCancelModal, setShowCancelModal] = useState<boolean>(false);
  const [showUncancelModal, setShowUncancelModal] = useState<boolean>(false);
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [setPrimaryModalApplicant, setSetPrimaryModalApplicant] = useState<
    Applicant | undefined
  >(undefined);
  const [changeRoleModalApplicant, setChangeRoleModalApplicant] = useState<
    Applicant | undefined
  >(undefined);
  const [resendInviteModalInvite, setResendInviteModalInvite] = useState<
    Invitee | undefined
  >(undefined);
  const [documentRequestModalApplicant, setDocumentRequestModalApplicant] =
    useState<string | undefined>(undefined);
  const {
    applicationMeta,
    applicationDecision,
    applicantDetail,
    searchParams,
    setSearchParams,
    queryKey
  } = useApplicantIdReportController();

  const queryClient = useQueryClient();

  const applicationId = applicationMeta.data?.id;
  const { mutate: changeRole } = useMutation({
    mutationKey: ["CHANGE_ROLE", applicationId],
    mutationFn: (role: ApplicantType) =>
      instance.post(
        `/portal/v1/applications/${applicationId}/applicants/${changeRoleModalApplicant?.id}/change_role`,
        {
          role: role
        }
      ),
    onSuccess: () => {
      toast.success("Role updated successfully");
      setChangeRoleModalApplicant(undefined);

      queryClient.invalidateQueries({
        queryKey: queryKey
      });
    },
    onError: (err: Error) => {
      toast.error(getErrorMessage(err));
      setChangeRoleModalApplicant(undefined);
    }
  });

  // Only use the full applicant detail from the per-applicant endpoint.
  // The summary's applicants are slim (no verifications) and would crash
  // downstream components that expect verification data.
  const applicant = useMemo(() => {
    if (
      applicantDetail.data &&
      String(applicantDetail.data.id) === String(searchParams.applicantId)
    )
      return applicantDetail.data;

    return undefined;
  }, [searchParams.applicantId, applicantDetail.data]);

  const availableRoles = useMemo(() => {
    const roles = [ApplicantType.Applicant];
    const settings = applicationMeta.data?.verification_template?.settings;
    if (settings?.co_signer?.enabled_screening_services)
      roles.push(ApplicantType.CoSigner);
    if (settings?.occupant?.enabled_screening_services)
      roles.push(ApplicantType.Occupant);
    return roles;
  }, [applicationMeta.data]);

  const verificationSchemas = applicant?.role
    ? applicationMeta.data?.verification_template?.verification_schemas[
        applicant.role
      ]
    : undefined;
  const applicationSchemaNames = Object.keys(verificationSchemas || {});
  // Applicant may use template that is different from the property template, which causes available reports discrepancy
  const hasVerificationsMismatch =
    (
      applicant?.verifications.filter(
        (v) => !applicationSchemaNames.includes(v.name)
      ) || []
    ).length > 0;

  const { type, partner, permissions } = useAuthContext();

  const premiumRentalVerificationEnabled =
    partner?.features.includes(FEATURES.WIP_FEAT_PREMIUM_RENTAL_VERIFICATION) ??
    false;

  const applicationVerifications = useMemo(() => {
    if (!verificationSchemas) return [];
    if (hasVerificationsMismatch && applicant?.verifications) {
      if (!premiumRentalVerificationEnabled)
        return applicant.verifications.filter(
          (v) => v.name !== VerificationNames.PremiumRentalHistory
        );
      return applicant.verifications;
    }
    return Object.entries(verificationSchemas)
      .filter(([v, vValue]) => {
        if (
          !premiumRentalVerificationEnabled &&
          v === VerificationNames.PremiumRentalHistory
        )
          return false;
        // Warnings report has no applicant-facing step, so a schema entry
        // alone doesn't mean the report exists — show it only when the
        // applicant actually has one.
        if (
          v === VerificationNames.WarningsReport &&
          !applicant?.verifications.some(
            (av) => av.name === VerificationNames.WarningsReport
          )
        )
          return false;
        return filterVerificationByConfigValue({
          verificationName: v,
          verificationValue: vValue
        });
      })
      .map((v) => v[0])
      .map((name) => {
        const existingVerification = applicant?.verifications.find(
          (v) => v.name === name
        );
        if (!existingVerification)
          return {
            id: `missing-${name}`,
            name,
            title: verificationNameToTitle(name as VerificationNames),
            status: VerificationStatus.NoDataAvailable
          };
        else return existingVerification;
      });
  }, [
    applicant?.verifications,
    verificationSchemas,
    hasVerificationsMismatch,
    premiumRentalVerificationEnabled
  ]);

  const mergedNavVerifications = useMemo(() => {
    const filtered = (applicationVerifications as Verification[]).filter(
      (v) =>
        v.name !== VerificationNames.TermsAndConditions &&
        v.name !== VerificationNames.RentalVerification
    );
    return mergeHousingHistoryVerifications(filtered).filter(
      (v) => v.name !== VerificationNames.PremiumRentalHistory
    );
  }, [applicationVerifications]);

  const requestedReport =
    (searchParams.report as TabStepsType) ||
    VerificationNames.RentalApplication;

  // Reports the currently selected applicant can actually view: their
  // role-driven verifications plus the always-available tabs.
  const availableReports = useMemo(() => {
    const names = new Set<TabStepsType>(ALWAYS_AVAILABLE_REPORTS);
    mergedNavVerifications.forEach((v) => names.add(v.name as TabStepsType));
    return names;
  }, [mergedNavVerifications]);

  const isRequestedReportAvailable = availableReports.has(requestedReport);

  // When switching applicants we keep the same report in the URL. If the newly
  // selected applicant doesn't have it (e.g. an occupant without income
  // verification), show the rental application, which every applicant has.
  const currentTab: TabStepsType = isRequestedReportAvailable
    ? requestedReport
    : VerificationNames.RentalApplication;

  // Keep the URL honest so refreshes / deep links land on a report the
  // applicant actually has. Guarded on `applicant` so it only runs once the
  // full detail (with reliable verifications) has loaded.
  useEffect(() => {
    if (!applicant || isRequestedReportAvailable) return;

    setSearchParams({
      ...searchParams,
      report: VerificationNames.RentalApplication
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- setSearchParams identity is unstable; searchParams is current for the loaded applicant
  }, [applicant, isRequestedReportAvailable]);

  const currentVerification = mergedNavVerifications.find(
    (verification) => verification.name === currentTab
  );

  const requiresOverridesIdentityVerification = Boolean(
    applicationDecision.data?.requires_overrides_identity_verification?.applicants.some(
      ({ id }) => id === applicant?.id
    )
  );

  const verifications = combineVerifications<Verification>(
    sortVerifications(mergedNavVerifications, VERIFICATIONS_ORDER),
    [
      [
        VerificationNames.IncomeVerification,
        VerificationNames.VerifiedIncomeVerification
      ]
    ]
  );

  if (!type.isBoomScreen) return <Redirect href="/" />;

  if (applicationMeta.isLoading || applicantDetail.isLoading)
    return (
      <div className="flex h-full w-full items-center justify-center">
        <Loader />
      </div>
    );

  if (!applicant) {
    if (!applicationMeta.data) return <Redirect href="/" />;

    return null;
  }

  return (
    <Layout
      headerClassName="bg-white border-b border-alpha-100"
      sectionIdentifier="report_section"
      header={
        <LayoutHeader
          applicationMeta={applicationMeta}
          makeDecisionRequirements={
            applicationDecision.data?.make_decision_requirements
          }
          searchParams={searchParams}
          setSearchParams={setSearchParams}
          templateId={applicationMeta.data?.verification_template?.id || ""}
        />
      }
    >
      <FetchBoundary error={applicationMeta.error}>
        <PrintProvider>
          <ApplicationReportLayout
            applicant={applicant}
            currentTab={currentTab}
            currentVerification={currentVerification}
            verifications={verifications}
            // @ts-expect-error
            verificationSchemas={verificationSchemas}
            setShowCancelModal={setShowCancelModal}
            setShowUncancelModal={setShowUncancelModal}
            setShowRefundModal={setShowRefundModal}
            setChangeRoleModalApplicant={setChangeRoleModalApplicant}
            setSetPrimaryModalApplicant={setSetPrimaryModalApplicant}
            setResendInviteModalInvite={setResendInviteModalInvite}
            setDocumentRequestModalApplicant={setDocumentRequestModalApplicant}
            requiresOverridesIdentityVerification={
              requiresOverridesIdentityVerification
            }
          />
          <Show if={showCancelModal}>
            <CancelOrRefundModal
              applicationId={
                applicationMeta.data?.id ? String(applicationMeta.data.id) : ""
              }
              applicantId={applicant.id ? String(applicant.id) : ""}
              onSuccess={() => applicationMeta.refetch()}
              onClose={() => setShowCancelModal(false)}
            />
          </Show>
          <Show if={showUncancelModal}>
            <UncancelModal
              applicationId={
                applicationMeta.data?.id ? String(applicationMeta.data.id) : ""
              }
              applicantId={applicant.id ? String(applicant.id) : ""}
              onSuccess={() => applicationMeta.refetch()}
              onClose={() => setShowUncancelModal(false)}
            />
          </Show>
          <RefundModal
            applicationId={
              showRefundModal && applicationMeta.data?.id
                ? String(applicationMeta.data.id)
                : ""
            }
            applicantId={
              showRefundModal && applicant.id ? String(applicant.id) : ""
            }
            onClose={() => setShowRefundModal(false)}
            queryKey={queryKey}
          />
          <Show if={Boolean(setPrimaryModalApplicant)}>
            <SetAsPrimaryModal
              applicantName={
                [
                  setPrimaryModalApplicant?.first_name,
                  setPrimaryModalApplicant?.last_name
                ]
                  .filter(Boolean)
                  .join(" ") ||
                setPrimaryModalApplicant?.email ||
                "Applicant"
              }
              applicantId={String(setPrimaryModalApplicant?.id || "")}
              applicationId={String(applicationMeta.data?.id || "")}
              onClose={() => setSetPrimaryModalApplicant(undefined)}
              onSuccess={() => {
                queryClient.invalidateQueries({
                  queryKey: queryKey
                });
                setSetPrimaryModalApplicant(undefined);
              }}
            />
          </Show>
          <Show if={Boolean(changeRoleModalApplicant)}>
            <ChangeRoleModal
              availableRoles={availableRoles}
              onClose={() => setChangeRoleModalApplicant(undefined)}
              onChange={(selectedRole) => {
                changeRole(selectedRole);
              }}
              applicantsList={applicationMeta.data?.applicants || []}
              isOpen={Boolean(changeRoleModalApplicant)}
              applicant={changeRoleModalApplicant}
            />
          </Show>
          <Show if={Boolean(resendInviteModalInvite)}>
            <ResendInviteModal
              onClose={() => setResendInviteModalInvite(undefined)}
              onSuccess={() => {
                queryClient.invalidateQueries({
                  queryKey: queryKey
                });

                setResendInviteModalInvite(undefined);
              }}
              pendingMagicLink={resendInviteModalInvite as Invitee}
              applicationId={String(applicationMeta.data?.id || "")}
              canCopyApplicationLink={permissions.canCopyApplicationLink}
            />
          </Show>
          <Show if={Boolean(documentRequestModalApplicant)}>
            <DocumentsRequestModal
              onClose={() => setDocumentRequestModalApplicant(undefined)}
              applicationId={String(applicationMeta.data?.id || "")}
              applicantId={String(documentRequestModalApplicant)}
              queryKey={queryKey}
            />
          </Show>
        </PrintProvider>
      </FetchBoundary>
    </Layout>
  );
};
